import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type RepoPickerOption = {
  fullName: string;
  name: string;
  private: boolean;
  defaultBranch: string | null;
  description: string | null;
  pushedAt: string | null;
  ownerLogin: string;
  ownerType: string;
};

type Props = {
  repos: RepoPickerOption[];
  value: string;
  onChange: (fullName: string) => void;
  viewerLogin: string | null;
};

type Group = {
  key: string;
  label: string;
  kind: 'personal' | 'org';
  repos: RepoPickerOption[];
};

type TFn = (k: string, def?: string) => string;

function formatRelative(iso: string | null, t: TFn): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day;
  const year = 365 * day;
  if (diff < hour) return t('repoPicker.justNow', 'just now');
  if (diff < day) return t('repoPicker.hoursAgo', '{{n}}h ago').replace('{{n}}', String(Math.floor(diff / hour)));
  if (diff < week) return t('repoPicker.daysAgo', '{{n}}d ago').replace('{{n}}', String(Math.floor(diff / day)));
  if (diff < month) return t('repoPicker.weeksAgo', '{{n}}w ago').replace('{{n}}', String(Math.floor(diff / week)));
  if (diff < year) return t('repoPicker.monthsAgo', '{{n}}mo ago').replace('{{n}}', String(Math.floor(diff / month)));
  return t('repoPicker.yearsAgo', '{{n}}y ago').replace('{{n}}', String(Math.floor(diff / year)));
}

export default function RepoPicker({ repos, value, onChange, viewerLogin }: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return repos;
    return repos.filter(
      (r) =>
        r.fullName.toLowerCase().includes(q) ||
        (r.description ?? '').toLowerCase().includes(q)
    );
  }, [repos, query]);

  const groups = useMemo<Group[]>(() => {
    const byOwner = new Map<string, RepoPickerOption[]>();
    for (const repo of filtered) {
      const list = byOwner.get(repo.ownerLogin) ?? [];
      list.push(repo);
      byOwner.set(repo.ownerLogin, list);
    }

    const sortByPushed = (a: RepoPickerOption, b: RepoPickerOption) => {
      const ta = a.pushedAt ? Date.parse(a.pushedAt) : 0;
      const tb = b.pushedAt ? Date.parse(b.pushedAt) : 0;
      return tb - ta;
    };

    const result: Group[] = [];
    if (viewerLogin && byOwner.has(viewerLogin)) {
      result.push({
        key: viewerLogin,
        label: t('repoPicker.personal', 'Personal'),
        kind: 'personal',
        repos: (byOwner.get(viewerLogin) ?? []).slice().sort(sortByPushed),
      });
      byOwner.delete(viewerLogin);
    }
    const orgKeys = Array.from(byOwner.keys()).sort((a, b) => a.localeCompare(b));
    for (const owner of orgKeys) {
      result.push({
        key: owner,
        label: owner,
        kind: 'org',
        repos: (byOwner.get(owner) ?? []).slice().sort(sortByPushed),
      });
    }
    return result;
  }, [filtered, viewerLogin, t]);

  const totalCount = filtered.length;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="border-b border-gray-200 dark:border-gray-700 p-2">
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('repoPicker.searchPlaceholder', 'Search repositories…')}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-800 border border-transparent focus:outline-none focus:bg-white dark:focus:bg-gray-900 focus:border-primary-500 text-gray-900 dark:text-gray-100 placeholder:text-gray-400"
          />
        </div>
      </div>

      <div className="max-h-80 overflow-y-auto">
        {totalCount === 0 && (
          <div className="px-4 py-8 text-center text-sm text-gray-500">
            {query
              ? t('repoPicker.noMatches', 'No repositories match your search.')
              : t('repoPicker.empty', 'No repositories available.')}
          </div>
        )}

        {groups.map((group) => (
          <div key={group.key}>
            <div className="sticky top-0 z-10 flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-gray-800/80 backdrop-blur border-b border-gray-200 dark:border-gray-700">
              {group.kind === 'personal' ? (
                <svg className="w-3.5 h-3.5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 4a1 1 0 011-1h14a1 1 0 011 1v12a1 1 0 01-1 1H3a1 1 0 01-1-1V4zm3 2v2h2V6H5zm0 4v2h2v-2H5zm0 4v2h2v-2H5zm4-8v2h2V6H9zm0 4v2h2v-2H9zm0 4v2h2v-2H9zm4-8v2h2V6h-2zm0 4v2h2v-2h-2zm0 4v2h2v-2h-2z" />
                </svg>
              )}
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                {group.label}
              </span>
              <span className="text-xs text-gray-400">{group.repos.length}</span>
            </div>
            <ul>
              {group.repos.map((repo) => {
                const selected = repo.fullName === value;
                return (
                  <li key={repo.fullName}>
                    <button
                      type="button"
                      onClick={() => onChange(repo.fullName)}
                      className={`w-full text-left px-3 py-2.5 flex items-start gap-3 border-b border-gray-100 dark:border-gray-800 transition-colors ${
                        selected
                          ? 'bg-primary-50 dark:bg-primary-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'
                      }`}
                    >
                      <span
                        className={`mt-1 inline-block w-1.5 h-1.5 rounded-full flex-none ${
                          selected ? 'bg-primary-600' : 'bg-transparent'
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-sm font-medium truncate ${selected ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-gray-100'}`}>
                            {repo.name}
                          </span>
                          {repo.private && (
                            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                              {t('repoPicker.private', 'Private')}
                            </span>
                          )}
                          {repo.pushedAt && (
                            <span className="text-xs text-gray-500">{formatRelative(repo.pushedAt, t as unknown as TFn)}</span>
                          )}
                        </div>
                        {repo.description && (
                          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate">
                            {repo.description}
                          </div>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
